import type { PaymentMethod } from "@onestore/api/basket"
import type { PaymentModalData } from "@onestore/api/types"
import type { HTTP, PaymentRedirectData } from "@onestore/onestore-store-common"
import { EXISTING_PAYTOOL_TYPE } from "@onestore/onestore-store-common/api/payments"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import type { Draft } from "immer"
import { produce } from "immer"
import type { BasketAction } from "~/store/basket/actions"
import { BasketActions } from "~/store/basket/constants"
import type { CheckoutAction } from "~/store/checkout/actions"
import { CheckoutActions } from "~/store/checkout/constants"
import type { CheckoutTermsAction } from "~/store/checkout-terms/actions"
import { CheckoutTermsActionType } from "~/store/checkout-terms/constants"
import type { OrderActions } from "~/store/order/actions"
import { OrderActionType } from "~/store/order/constants"
import type { QuickCheckoutSummaryActions } from "~/store/quick-checkout/actions"
import { QuickCheckoutActionType } from "~/store/quick-checkout/constants"

export type OrderState = {
  // status of the terms-acceptance request
  readonly order_request: HTTP.Status
  // Payment information.
  readonly payment_system_id?: string
  readonly order_id?: number
  readonly order_number?: string
  readonly redirect_data: PaymentRedirectData | null
  readonly modal_form?: PaymentModalData
  readonly is_modal_based_payment: boolean
  readonly bank_transfer_data?: {
    accountNumber: string | null
    recipient: string[]
    title: string | null
  }

  readonly is_existing_payment_tool?: boolean
  readonly total_value: number
  readonly total_vat_value: number

  readonly payment_method: number | null
  readonly payment_methods: PaymentMethod[]
}

export const initialState: OrderState = {
  // status of the terms-acceptance request
  order_request: HTTP_STATUS.NONE,

  order_number: undefined,

  // Payment information.
  order_id: undefined,
  redirect_data: null,
  modal_form: undefined,
  is_modal_based_payment: false,
  bank_transfer_data: undefined,
  is_existing_payment_tool: undefined,
  total_value: 0,
  total_vat_value: 0,
  payment_method: null,
  payment_methods: [],
}

export default function reducer(
  state: OrderState = initialState,
  action:
    | OrderActions
    | CheckoutAction
    | CheckoutTermsAction
    | BasketAction
    | QuickCheckoutSummaryActions
): OrderState {
  return produce<OrderState, OrderState>(
    state,
    (nextState: Draft<OrderState>): void => {
      switch (action.type) {
        case BasketActions.BASKET_SUCCESS:
          nextState.payment_method = action.result.payment_method
          nextState.payment_methods = action.result.payment_methods

          return
        case CheckoutTermsActionType.TERMS_SUCCESS:
          nextState.payment_method = action.payment_method
          nextState.payment_methods = action.payment_methods

          return
        case QuickCheckoutActionType.SUMMARY_SUCCESS:
          nextState.payment_method = action.basket.payment_method
          nextState.payment_methods = action.basket.payment_methods

          return
        case CheckoutActions.CHECKOUT_RESET:
          nextState.order_request = HTTP_STATUS.NONE

          return
        case OrderActionType.PAYMENT_PENDING:
          nextState.order_request = HTTP_STATUS.CONTINUE

          return

        case OrderActionType.PAYMENT_SUCCESS:
        case QuickCheckoutActionType.PAYMENT_SUCCESS:
          nextState.is_modal_based_payment = Boolean(
            action.result.modal_form &&
              Object.values(action.result.modal_form).length !== 0
          )
          nextState.order_id = action.result.id
          nextState.order_number = action.result.number
          nextState.order_request = action.status
          nextState.redirect_data = action.result
            .redirect_data as PaymentRedirectData | null
          nextState.modal_form =
            (action.result.modal_form as PaymentModalData | null) ?? undefined
          nextState.bank_transfer_data = action.result.bank_transfer_data
          nextState.total_value = action.result.total_value
          nextState.total_vat_value = action.result.total_vat_value
          nextState.is_existing_payment_tool =
            EXISTING_PAYTOOL_TYPE === action.result.pay_tool_type

          const selectedPaymentMethod = action.payment_methods.filter(
            (item) => item.id === action.payment_method
          )

          if (selectedPaymentMethod.length === 1) {
            nextState.payment_system_id = selectedPaymentMethod[0].system_id
          }

          return

        case OrderActionType.ORDER_SUCCESS:
          nextState.order_request = action.response_status_code

          return
        case OrderActionType.ORDER_FAILURE:
          nextState.order_request = action.response_status_code

          return

        case OrderActionType.ORDER_PENDING:
          nextState.order_request = HTTP_STATUS.CONTINUE

          return

        case OrderActionType.PAYMENT_FAILURE:
          nextState.order_request = HTTP_STATUS.BAD_REQUEST

          return
        case OrderActionType.PAYMENT_METHOD_CHANGE: // TODO need to re-run the request with a different payment method ...
          nextState.payment_method = action.payment_method

          return
      }
    }
  )
}
